/**
 * Query keys, organised by garage.
 *
 * The garage is the tenant (§2), so it is the first thing after the resource
 * name in every key. That is not tidiness — it is what makes
 * `invalidateQueries({ queryKey: keys.garage(id) })` mean "everything I know
 * about this garage is stale" and, critically, what stops one garage's cache
 * answering another garage's question after the switcher flips. A key that
 * forgot its garage id would serve the wrong tenant's cars for as long as the
 * cache lived.
 *
 * Every key is built here rather than inlined at call sites, so an invalidation
 * and the query it means to invalidate cannot drift apart by a typo.
 */

import type { ListUsersQuery } from '@svcdue/contracts';

export const keys = {
  me: () => ['me'] as const,
  garages: () => ['garages'] as const,

  /** The prefix every garage-scoped key extends. Invalidate it to drop the lot. */
  garage: (garageId: string) => ['garage', garageId] as const,

  dashboard: (garageId: string) => ['garage', garageId, 'dashboard'] as const,
  /**
   * `carId` is part of the key because the API filters server-side — an
   * unfiltered timeline and a one-car timeline are different answers, and
   * sharing a cache entry between them would show the whole fleet on a car page.
   */
  timeline: (garageId: string, carId?: string) => ['garage', garageId, 'timeline', carId ?? 'all'] as const,
  history: (garageId: string) => ['garage', garageId, 'history'] as const,
  cars: (garageId: string) => ['garage', garageId, 'cars'] as const,
  drivers: (garageId: string) => ['garage', garageId, 'drivers'] as const,
  items: (garageId: string) => ['garage', garageId, 'items'] as const,
  events: (garageId: string) => ['garage', garageId, 'events'] as const,
  members: (garageId: string) => ['garage', garageId, 'members'] as const,
  serviceLogs: (garageId: string, itemId: string) => ['garage', garageId, 'items', itemId, 'logs'] as const,

  /** Keyed by token, not garage id — the public view never learns the id. */
  publicGarage: (token: string) => ['public', token] as const,

  admin: {
    users: (query: ListUsersQuery) => ['admin', 'users', query.q ?? '', query.limit, query.offset] as const,
    user: (userId: string) => ['admin', 'users', userId] as const,
    stats: (days: number) => ['admin', 'stats', days] as const,
    costs: (days: number) => ['admin', 'costs', days] as const,
  },
} as const;
